"use client";

import type React from "react";
import { useCallback, useRef, useState } from "react";
import { Upload } from "lucide-react";

interface FileDropZoneProps {
	accept: string;
	multiple?: boolean;
	disabled?: boolean;
	onFiles: (files: FileList | File[]) => void;
	label?: string;
	activeLabel?: string;
	ariaLabel?: string;
}

function processEntry(entry: FileSystemEntry): Promise<File[]> {
	return new Promise((resolve) => {
		if (entry.isFile) {
			(entry as FileSystemFileEntry).file(
				(file) => resolve([file]),
				() => resolve([]),
			);
		} else if (entry.isDirectory) {
			const reader = (
				entry as FileSystemDirectoryEntry
			).createReader();
			reader.readEntries(async (entries) => {
				const nested = await Promise.all(entries.map(processEntry));
				resolve(nested.flat());
			});
		} else {
			resolve([]);
		}
	});
}

export function FileDropZone({
	accept,
	multiple = false,
	disabled = false,
	onFiles,
	label = "Drop files here, or click to browse",
	activeLabel = "Drop to add",
	ariaLabel,
}: FileDropZoneProps) {
	const [isDragging, setIsDragging] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const handleDrop = useCallback(
		(event: React.DragEvent<HTMLDivElement>) => {
			event.preventDefault();
			setIsDragging(false);
			if (disabled) return;

			const items = event.dataTransfer.items;

			// Try webkitGetAsEntry for folder support
			if (items) {
				const entries: FileSystemEntry[] = [];
				for (let i = 0; i < items.length; i++) {
					const entry = items[i].webkitGetAsEntry?.();
					if (entry) entries.push(entry);
				}
				if (entries.length > 0) {
					Promise.all(entries.map(processEntry)).then((nested) => {
						const allFiles = nested.flat();
						if (allFiles.length > 0) onFiles(allFiles);
					});
					return;
				}
			}

			// Fallback
			if (event.dataTransfer.files.length > 0) {
				onFiles(event.dataTransfer.files);
			}
		},
		[disabled, onFiles],
	);

	const handleDragOver = useCallback(
		(event: React.DragEvent<HTMLDivElement>) => {
			event.preventDefault();
			if (!disabled) setIsDragging(true);
		},
		[disabled],
	);

	const handleDragLeave = useCallback(
		(event: React.DragEvent<HTMLDivElement>) => {
			event.preventDefault();
			setIsDragging(false);
		},
		[],
	);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const fileList = event.target.files;
			if (fileList && fileList.length > 0) {
				onFiles(fileList);
			}
			event.target.value = "";
		},
		[onFiles],
	);

	return (
		<>
			<div
				onDrop={handleDrop}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onClick={() => !disabled && inputRef.current?.click()}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						if (!disabled) inputRef.current?.click();
					}
				}}
				role="button"
				tabIndex={0}
				aria-label={ariaLabel || label}
				className={`
					border-2 border-dashed rounded-lg p-4 sm:p-8 text-center cursor-pointer
					transition-colors duration-200
					${isDragging
						? "border-primary bg-primary/10"
						: "border-muted-foreground/30 hover:border-primary hover:bg-muted/50"
					}
					${disabled ? "opacity-50 cursor-not-allowed" : ""}
				`}
			>
				<Upload
					className="w-10 h-10 mx-auto mb-3 text-muted-foreground"
					aria-hidden="true"
				/>
				<p className="text-sm font-bold text-muted-foreground">
					{isDragging ? activeLabel : label}
				</p>
			</div>
			<input
				ref={inputRef}
				type="file"
				multiple={multiple}
				accept={accept}
				onChange={handleChange}
				className="hidden"
				disabled={disabled}
				aria-hidden="true"
				tabIndex={-1}
			/>
		</>
	);
}
